<template>
    <card>
        <h2 slot="header" class="mb-2">Edit User</h2>
        <ValidationObserver ref="form" v-slot="{ invalid, validate }">
            <form
                autocomplete="off"
                @submit.prevent="validate().then(updateUser)"
            >
                <div class="row">
                    <div class="col-xl-4 col-6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="First Name"
                            rules="required"
                        >
                            <base-input
                                v-model.trim="userEdit.first_name"
                                name="fname"
                                autocomplete="off"
                                type="text"
                                label="First Name"
                                placeholder="First Name"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="col-xl-4 col-6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Last Name"
                            rules="required"
                        >
                            <base-input
                                v-model.trim="userEdit.last_name"
                                name="lname"
                                autocomplete="off"
                                type="text"
                                label="Last Name"
                                placeholder="Last Name"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="col-xl-4 col-12">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Email"
                            rules="email"
                        >
                            <base-input
                                v-model.trim="userEdit.email"
                                name="email"
                                type="email"
                                label="Email address"
                                placeholder="Email"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                </div>
                <div class="row">
                    <div class="col-4">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Username"
                            rules="required"
                        >
                            <base-input
                                v-model.trim="userNameLower"
                                name="username"
                                type="text"
                                label="Username"
                                placeholder="Username"
                                autocomplete="new-username"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                    <div class="col-4">
                        <label>Role</label>
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Role"
                            rules="required"
                        >
                            <v-select
                                v-model="userEdit.role"
                                :options="userRoles"
                                placeholder="Select role"
                                label="label"
                            >
                                <template slot="option" slot-scope="option">
                                    {{ option.label }}
                                </template>
                            </v-select>
                            <label class="error">{{ errors[0] }}</label>
                        </ValidationProvider>
                    </div>
                    <div class="col-4">
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="Password"
                            :rules="`min:${passwordMinLength}`"
                        >
                            <base-input
                                v-model.trim="userEdit.password"
                                unlock
                                name="password"
                                type="password"
                                label="Password"
                                placeholder="********"
                                autocomplete="new-password"
                                :error="errors[0]"
                            />
                        </ValidationProvider>
                    </div>
                </div>
                <div class="row">
                    <div v-if="showSelectCustomer" class="col-4">
                        <ValidationProvider>
                            <label>Customer</label>
                            <div class="form-group">
                                <v-select
                                    v-model="selectedCustomer"
                                    placeholder="Select customer"
                                    label="name"
                                    autocomplete="off"
                                    :options="customers"
                                    @search="searchCustomers(...arguments)"
                                >
                                    <template slot="no-options">
                                        <span v-if="searchCustomersText">
                                            No results found.
                                        </span>
                                        <span v-else>Type to search...</span>
                                    </template>
                                    <template slot="option" slot-scope="option">
                                        {{ option?.name }}
                                    </template>
                                </v-select>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div v-if="showSelectDesigner" class="col-4">
                        <ValidationProvider>
                            <label>Designer</label>
                            <div class="form-group">
                                <v-select
                                    v-model="selectedDesigner"
                                    label="fullName"
                                    placeholder="Select designer"
                                    :options="designers"
                                    @search="searchDesigners(...arguments)"
                                >
                                    <template slot="no-options">
                                        <span v-if="searchDesignersText">
                                            No results found.
                                        </span>
                                        <span v-else>Type to search...</span>
                                    </template>
                                    <template
                                        slot="selected-option"
                                        slot-scope="option"
                                    >
                                        {{ option?.fullName }} -
                                        {{ option?.customer?.name }}
                                    </template>
                                    <template slot="option" slot-scope="option">
                                        <div>
                                            <b>{{ option?.fullName }}</b>
                                        </div>
                                        <div>
                                            {{ option?.customer?.name }}
                                        </div>
                                    </template>
                                </v-select>
                            </div>
                        </ValidationProvider>
                    </div>
                    <div class="col-4">
                        <label>Allow user to login</label>
                        <div class="form-group has-label">
                            <el-switch
                                v-model="userStatus"
                                active-text="Yes"
                                inactive-text="No"
                            />
                        </div>
                    </div>
                    <div class="col-4">
                        <base-button
                            type="info"
                            class="btn-fill"
                            @click="showAdditionalUserModal = true"
                        >
                            Add Another Account
                        </base-button>
                    </div>
                </div>
                <base-button
                    id="updateUser"
                    :disabled="isUnModified || invalid"
                    native-type="submit"
                    type="primary"
                    class="btn-fill"
                >
                    Update User
                </base-button>
            </form>
        </ValidationObserver>
        <modal
            :show.sync="showAdditionalUserModal"
            body-classes="p-0"
            modal-classes="modal-sm"
        >
            <card class="mb-0 card-form">
                <template slot="header">
                    <h2 class="modal-title">Add User</h2>
                    <button
                        type="button"
                        class="close"
                        aria-label="Close"
                        @click="showAdditionalUserModal = false"
                    >
                        <i class="tim-icons icon-simple-remove" />
                    </button>
                </template>
                <user-add-form
                    v-if="showAdditionalUserModal"
                    :customers="customers"
                    :designers="designers"
                    :user-roles="userRoles"
                    :password-min-length="passwordMinLength"
                    :user-details="userEdit"
                    :user-added="userAdded"
                    @searchDesigners="$emit('searchDesigners', $event)"
                    @searchCustomers="$emit('searchCustomers', $event)"
                    @close="showAdditionalUserModal = false"
                    @createUser="$emit('createAdditionalUser', $event)"
                />
            </card>
        </modal>
    </card>
</template>
<script>
import _ from "lodash";
import vSelect from "vue-select";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Switch } from "element-ui";
import { Modal } from "src/components/UI";
import UserAddForm from "src/modules/User/Users/_components/UserAddForm.vue";

export default {
    name: "UserEditForm",
    components: {
        Modal,
        ValidationObserver,
        ValidationProvider,
        vSelect,
        [Switch.name]: Switch,
        UserAddForm,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        customers: {
            type: Array,
            required: true,
        },
        designers: {
            type: Array,
            required: true,
        },
        userRoles: {
            type: Array,
            required: true,
        },
        passwordMinLength: {
            type: Number,
            required: true,
        },
        userAdded: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            searchDesignersText: null,
            searchCustomersText: null,
            showSelectCustomer: false,
            showSelectDesigner: false,
            showAdditionalUserModal: false,
            userEdit: { ...this.user },
        };
    },
    computed: {
        isUnModified() {
            return _.isEqual(this.user, this.userEdit);
        },
        userNameLower: {
            get() {
                return this.userEdit.username;
            },
            set(value) {
                this.userEdit.username = value.toLowerCase();
            },
        },
        selectedDesigner: {
            get() {
                return {
                    id: this.userEdit.designer_id,
                    fullName: this.userEdit.designer_name,
                    customer: {
                        id: this.userEdit.customer_id,
                        name: this.userEdit.customer_name,
                    },
                };
            },
            set(value) {
                this.userEdit.customer_id = value?.customer.id;
                this.userEdit.customer_name = value?.customer.name;
                this.userEdit.designer_id = value?.id;
                this.userEdit.designer_name = value?.fullName;
            },
        },
        selectedCustomer: {
            get() {
                return {
                    id: this.userEdit.customer_id,
                    name: this.userEdit.customer_name,
                };
            },
            set(value) {
                this.userEdit.customer_id = value?.id;
                this.userEdit.customer_name = value?.name;
            },
        },
        userStatus: {
            get() {
                return this.userEdit.status === "Active";
            },
            set(active) {
                this.userEdit.status = active ? "Active" : "InActive";
            },
        },
        designerPlaceholder() {
            return this.selectedCustomer
                ? "Clear customer to select"
                : "Select designer";
        },
        customerPlaceholder() {
            return this.selectedDesigner
                ? "Clear designer to select"
                : "Select customer";
        },
    },
    watch: {
        "userEdit.role": function update() {
            this.updateRoleOptions();
        },
    },
    created() {
        this.updateRoleOptions(false);
    },
    methods: {
        updateUser() {
            this.$emit("updateUser", this.getChangedFields());
            this.$refs.form.reset();
        },
        updateRoleOptions(update = true) {
            switch (this.userEdit.role) {
                case "ClientBasic":
                    this.showSelectCustomer = false;
                    this.showSelectDesigner = true;
                    break;
                case "ClientAdmin":
                    this.showSelectCustomer = true;
                    this.showSelectDesigner = false;
                    break;
                default:
                    this.showSelectCustomer = false;
                    this.showSelectDesigner = false;
            }
            if (update) {
                // clear out customer id and name when a new role is selected
                this.userEdit.customer_id = null;
                this.userEdit.customer_name = null;
                this.userEdit.designer_id = null;
                this.userEdit.designer_name = null;
            }
        },
        searchDesigners(search, loading) {
            this.searchDesignersText = search;
            this.$emit("searchDesigners", { search, loading });
        },
        searchCustomers(search, loading) {
            this.searchCustomersText = search;
            this.$emit("searchCustomers", { search, loading });
        },
        getChangedFields() {
            return _.transform(
                this.userEdit,
                (result, value, key) => {
                    if (!_.isEqual(value, this.user[key])) {
                        result[key] = value;
                    }
                },
                {},
            );
        },
    },
};
</script>
